import React, { useState, useEffect, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createTenantPic, getTenantById } from "action/tenantAction";
import { getPackageById } from "action/packageAction";
import apiClient from "utils/apiClient";

/* ── Icons ── */
const ArrowLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);
const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
  </svg>
);
const BuildingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
  </svg>
);
const TagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

/* ── Info row: label + value (no input boxes) ── */
function InfoRow({ label, value, children, last }) {
  return (
    <div style={{
      display: "flex", gap: 16, padding: "11px 0",
      borderBottom: last ? "none" : "1px solid var(--border-subtle)",
    }}>
      <span style={{ width: 160, flexShrink: 0, fontSize: 12.5, color: "var(--text-muted)", fontWeight: 500, paddingTop: 1, lineHeight: 1.5 }}>
        {label}
      </span>
      <div style={{ flex: 1, fontSize: 13.5, color: "var(--text-strong)", fontWeight: 500, wordBreak: "break-word", lineHeight: 1.5 }}>
        {children ?? (value != null && value !== "" ? String(value) : <span style={{ color: "var(--border-strong)", fontWeight: 400 }}>—</span>)}
      </div>
    </div>
  );
}

/* ── Section card ── */
function SectionCard({ icon, title, action, children }) {
  return (
    <div className="lms-card" style={{ marginBottom: 0 }}>
      <div className="lms-card-header">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {icon && <span style={{ color: "var(--brand-500)" }}>{icon}</span>}
          <div className="t-h2" style={{ fontSize: 14.5 }}>{title}</div>
        </div>
        {action}
      </div>
      <div className="lms-card-body" style={{ padding: "6px 22px 16px" }}>{children}</div>
    </div>
  );
}

/* ── Contact row for PIC / Invitation lists ── */
const AVATAR_COLORS = ["#4F46E5","#0EA5E9","#10B981","#F59E0B","#EF4444","#8B5CF6","#EC4899","#14B8A6"];
function ContactRow({ name, email, phone, badge, index, last }) {
  const bg = AVATAR_COLORS[(index || 0) % AVATAR_COLORS.length];
  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "12px 0",
      borderBottom: last ? "none" : "1px solid var(--border-subtle)",
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10, background: bg, flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: "#fff", fontWeight: 700, fontSize: 12, letterSpacing: "0.03em",
        boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
      }}>
        {initials}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-strong)" }}>{name || "—"}</span>
          {badge}
        </div>
        {email && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{email}</div>}
        {phone && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{phone}</div>}
      </div>
    </div>
  );
}

/* ── Stat card (overview strip) ── */
function StatCard({ label, children, sub }) {
  return (
    <div className="lms-card" style={{ padding: "16px 18px", marginBottom: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", marginBottom: 10 }}>
        {label}
      </div>
      {children}
      {sub && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 7, lineHeight: 1.4 }}>{sub}</div>}
    </div>
  );
}

function TenantDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const toast = useToast();

  const [tenant, setTenant] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageLoading, setPackageLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isPicModalOpen, setIsPicModalOpen] = useState(false);
  const [picForm, setPicForm] = useState({ name: "", email: "", phone: "" });
  const [picSubmitting, setPicSubmitting] = useState(false);
  const [downloadingAttachment, setDownloadingAttachment] = useState(false);
  const [activeTab, setActiveTab] = useState("roles");
  const [tabData, setTabData] = useState({ roles: [], users: [], teachers: [], students: [] });
  const [tabMeta, setTabMeta] = useState({ roles: null, users: null, teachers: null, students: null });
  const [tabLoading, setTabLoading] = useState(false);

  const TABS = [
    { key: "roles", label: t("Roles") },
    { key: "users", label: t("Users") },
    { key: "teachers", label: t("Teachers") },
    { key: "students", label: t("Students") },
  ];

  const fetchTabData = useCallback(async (tab, page = 1) => {
    setTabLoading(true);
    try {
      const endpoints = {
        roles: `/tenants/${id}/tenant-roles`,
        users: `/tenants/${id}/tenant-users`,
        teachers: `/tenants/${id}/tenant-teachers`,
        students: `/tenants/${id}/tenant-students`,
      };
      const res = await apiClient.get(endpoints[tab], { params: { per_page: 10, page } });
      if (res.data?.status === "success") {
        setTabData((prev) => ({ ...prev, [tab]: res.data.data || [] }));
        setTabMeta((prev) => ({ ...prev, [tab]: res.data.meta || null }));
      }
    } catch {}
    setTabLoading(false);
  }, [id]);

  useEffect(() => {
    fetchTabData(activeTab);
  }, [activeTab, fetchTabData]);

  useEffect(() => {
    document.title = "Tenant Detail | LMS Tenant";
    const loadTenant = async () => {
      setLoading(true);
      try {
        const result = await getTenantById(id);
        if (result.success) setTenant(result.data);
      } catch {}
      finally { setLoading(false); }
    };
    loadTenant();
  }, [id]);

  useEffect(() => {
    const resolvePackage = async () => {
      if (!tenant) return;
      setPackageLoading(true);
      try {
        if (tenant.package) {
          const benefits = Array.isArray(tenant.package.details) ? tenant.package.details.map((d) => d?.detail).filter(Boolean) : tenant.package.package_detail || [];
          const price = typeof tenant.package.harga !== "undefined" ? parseFloat(tenant.package.harga) || 0 : tenant.package.price || 0;
          setSelectedPackage({ id: tenant.package.id, name: tenant.package.name, price, benefits, storage_gb: tenant.package.storage_gb || 0 });
          return;
        }
        if (tenant.id_package && tenant.id_package > 0) {
          const detail = await getPackageById(tenant.id_package);
          if (detail.success) { setSelectedPackage(detail.data); return; }
        }
        setSelectedPackage({ id: null, name: tenant.package?.name || "Custom Package", price: tenant.harga_package || 0, benefits: [], storage_gb: 0 });
      } finally {
        setPackageLoading(false);
      }
    };
    resolvePackage();
  }, [tenant]);

  const handleDownloadAttachment = async () => {
    const attachmentPath = tenant?.additional_attachment;
    const attachmentUrl = tenant?.additional_attachment_url || (attachmentPath ? `${window.location.origin}/storage/${attachmentPath.replace(/^storage\//, "")}` : null);
    if (!attachmentUrl) { toast({ title: t("Attachment not found"), status: "warning", duration: 2500, isClosable: true }); return; }
    const fileName = (attachmentPath || "attachment").split("/").pop() || "attachment";
    setDownloadingAttachment(true);
    try {
      const response = await fetch(attachmentUrl, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch attachment");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = fileName;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: t("Download started"), status: "success", duration: 2000, isClosable: true });
    } catch (e) {
      toast({ title: t("Download failed"), description: e?.message || t("Unable to download file"), status: "error", duration: 3000, isClosable: true });
    } finally {
      setDownloadingAttachment(false);
    }
  };

  const submitNewPic = async () => {
    if (!tenant) return;
    if (!picForm.name.trim() || !picForm.email.trim() || !picForm.phone.trim()) {
      toast({ title: t("Validation"), description: t("Name, Email, and Phone are required"), status: "warning", duration: 2500, isClosable: true });
      return;
    }
    setPicSubmitting(true);
    try {
      const res = await createTenantPic({ id_tenant: tenant.id, name: picForm.name, email: picForm.email, phone: picForm.phone });
      if (res.success) {
        toast({ title: t("Success"), description: res.message || t("PIC created."), status: "success", duration: 2500, isClosable: true });
        const refreshed = await getTenantById(tenant.id);
        if (refreshed.success) setTenant(refreshed.data);
        setIsPicModalOpen(false);
      } else {
        toast({ title: t("Error"), description: res.message || t("Failed to create PIC"), status: "error", duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: t("Error"), description: t("An error occurred while creating PIC"), status: "error", duration: 3000, isClosable: true });
    } finally {
      setPicSubmitting(false);
    }
  };

  const MONTHS_ID = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agt","Sep","Okt","Nov","Des"];
  const fmtDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return `${d.getDate()} ${MONTHS_ID[d.getMonth()]} ${d.getFullYear()}`;
  };

  if (loading) {
    return (
      <div className="lms-page lms-anim-fade">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 80, gap: 16 }}>
          <div className="lms-spinner" style={{ width: 36, height: 36 }} />
          <span className="t-body lms-text-muted">{t("Memuat data tenant...")}</span>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="lms-page lms-anim-fade">
        <div className="lms-empty">
          <div className="t-h3">{t("Tenant tidak ditemukan")}</div>
          <button className="lms-btn lms-btn-primary" style={{ marginTop: 16 }} onClick={() => history.push("/admin/master-tenant")}>{t("Kembali")}</button>
        </div>
      </div>
    );
  }

  const attachmentPath = tenant.additional_attachment;
  const attachmentUrl = tenant.additional_attachment_url || (attachmentPath ? `${window.location.origin}/storage/${attachmentPath.replace(/^storage\//, "")}` : null);
  const attachmentFileName = (attachmentPath || "").split("/").pop();

  const pkgBasePrice   = Number(tenant?.harga_package ?? selectedPackage?.price ?? 0) || 0;
  const pkgDiscountPct = Math.max(0, Math.min(100, Number(tenant?.diskon) || 0));
  const pkgDiscountedPrice = Math.round(pkgBasePrice * (1 - pkgDiscountPct / 100));
  const pkgInstallation = Number(tenant?.implementation_cost) || 0;
  const pkgTotal = Number(tenant?.total) || pkgDiscountedPrice + pkgInstallation;
  const displayBase = pkgBasePrice > 0 ? pkgBasePrice : pkgTotal;
  const pkgJumlahStudent = Number(tenant?.jumlah_student) || 0;
  const pkgStorageGb = Number(selectedPackage?.storage_gb) || 0;

  const isSubActive = tenant.tanggalBerakhir && new Date(tenant.tanggalBerakhir) >= new Date();
  const isTenantActive = tenant.status === "Active";

  return (
    <div className="lms-page lms-anim-fade">
      {/* ── Header ── */}
      <div className="lms-page-header">
        <div className="lms-page-header-text">
          <div className="lms-breadcrumb">
            <span style={{ cursor: "pointer" }} onClick={() => history.push("/admin/master-tenant")}>{t("Master Tenant")}</span>
            <ChevronRightIcon />
            <span className="lms-breadcrumb-current">{t("Detail Tenant")}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
            <div className="t-display">{tenant.namaTenant}</div>
            <span className={`lms-badge lms-badge-${isTenantActive ? "success" : "neutral"}`}>
              {isTenantActive ? "Active" : "Inactive"}
            </span>
          </div>
          {tenant.domainUnik && (
            <div style={{ fontFamily: "monospace", fontSize: 12.5, color: "var(--text-muted)", marginTop: 4 }}>{tenant.domainUnik}</div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="lms-btn lms-btn-secondary" onClick={() => history.push("/admin/master-tenant")}>
            <ArrowLeftIcon /> {t("Kembali")}
          </button>
          <button className="lms-btn lms-btn-secondary" onClick={() => { setPicForm({ name: "", email: "", phone: "" }); setIsPicModalOpen(true); }}>
            <PlusIcon /> {t("Tambah PIC")}
          </button>
          <button className="lms-btn lms-btn-primary" onClick={() => history.push(`/admin/edit-tenant/${tenant.id}`)}>
            <EditIcon /> {t("Edit Tenant")}
          </button>
        </div>
      </div>

      {/* ── Overview strip ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 14 }}>
        <StatCard
          label={t("Status Tenant")}
          sub={tenant.domainUnik}
        >
          <span className={`lms-badge lms-badge-${isTenantActive ? "success" : "neutral"}`}>
            {isTenantActive ? t("Active") : t("Inactive")}
          </span>
        </StatCard>

        <StatCard
          label={t("Subscription")}
          sub={`${fmtDate(tenant.tanggalMulai) || "—"}  →  ${fmtDate(tenant.tanggalBerakhir) || "—"}`}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className={`lms-badge lms-badge-${isSubActive ? "success" : "warning"}`}>
              {isSubActive ? t("Active") : t("Expired")}
            </span>
            {tenant.gracePeriod > 0 && (
              <span style={{ fontSize: 11, fontWeight: 600, background: "var(--warning-50)", color: "var(--warning-700)", borderRadius: 6, padding: "2px 7px" }}>
                Grace {tenant.gracePeriod}{t("h")}
              </span>
            )}
          </div>
        </StatCard>

        <StatCard
          label={t("Paket")}
          sub={pkgDiscountPct > 0 ? `${t("Diskon")} ${pkgDiscountPct}%` : null}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--brand-600)" }}>
            {tenant.subscriptionPlan || selectedPackage?.name || "—"}
          </div>
        </StatCard>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 }}>
        <StatCard
          label={t("Total Harga")}
          sub={pkgInstallation > 0 ? `${t("Termasuk implementasi")} Rp ${pkgInstallation.toLocaleString("id-ID")}` : null}
        >
          <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text-strong)", letterSpacing: "-0.01em" }}>
            Rp {pkgTotal.toLocaleString("id-ID")}
          </div>
        </StatCard>

        <StatCard
          label={t("Jumlah Student")}
          sub={t("Total student terdaftar dalam paket")}
        >
          <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text-strong)", letterSpacing: "-0.01em" }}>
            {pkgJumlahStudent > 0 ? pkgJumlahStudent.toLocaleString("id-ID") : "—"}
            {pkgJumlahStudent > 0 && <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-muted)", marginLeft: 4 }}>{t("siswa")}</span>}
          </div>
        </StatCard>

        <StatCard
          label={t("Storage")}
          sub={packageLoading ? t("Memuat...") : (pkgStorageGb > 0 ? t("Kapasitas penyimpanan paket") : t("Tidak ada informasi storage"))}
        >
          {packageLoading ? (
            <div className="lms-spinner" style={{ width: 16, height: 16 }} />
          ) : (
            <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text-strong)", letterSpacing: "-0.01em" }}>
              {pkgStorageGb > 0 ? (
                <>
                  {pkgStorageGb}
                  <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-muted)", marginLeft: 4 }}>GB</span>
                </>
              ) : "—"}
            </div>
          )}
        </StatCard>
      </div>

      {/* ── Content grid ── */}
      <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 20, alignItems: "start" }}>

        {/* ─ Left column ─ */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          <SectionCard icon={<BuildingIcon />} title={t("Informasi Dasar")}>
            <InfoRow label={t("Nama Perusahaan")} value={tenant.namaTenant} />
            <InfoRow label={t("Domain")}>
              <span style={{ fontFamily: "monospace", fontSize: 13 }}>{tenant.domainUnik || "—"}</span>
            </InfoRow>
            <InfoRow label={t("Email")} value={tenant.email} />
            <InfoRow label={t("No. Telepon")} value={tenant.nomorTelepon} />
            <InfoRow label={t("Alamat")} last>
              {tenant.alamat
                ? <span style={{ whiteSpace: "pre-wrap" }}>{tenant.alamat}</span>
                : <span style={{ color: "var(--border-strong)", fontWeight: 400 }}>—</span>}
            </InfoRow>
          </SectionCard>

          <SectionCard icon={<CalendarIcon />} title={t("Informasi Langganan")}>
            <InfoRow label={t("Paket")}>
              <span style={{ fontWeight: 600, color: "var(--brand-600)" }}>{tenant.subscriptionPlan || "—"}</span>
            </InfoRow>
            <InfoRow label={t("Status Langganan")}>
              <span className={`lms-badge lms-badge-${isSubActive ? "success" : "warning"}`}>
                {isSubActive ? t("Active") : t("Expired")}
              </span>
            </InfoRow>
            <InfoRow label={t("Tanggal Mulai")} value={fmtDate(tenant.tanggalMulai)} />
            <InfoRow label={t("Tanggal Berakhir")} value={fmtDate(tenant.tanggalBerakhir)} />
            <InfoRow label={t("Grace Period")}>
              {tenant.gracePeriod > 0
                ? <span style={{ fontSize: 12.5, fontWeight: 600, background: "var(--warning-50)", color: "var(--warning-700)", borderRadius: 6, padding: "2px 8px" }}>{tenant.gracePeriod} {t("hari")}</span>
                : <span style={{ color: "var(--border-strong)", fontWeight: 400 }}>—</span>}
            </InfoRow>
            <InfoRow label={t("Diskon")}>
              {pkgDiscountPct > 0
                ? <span className="lms-badge lms-badge-purple lms-badge-no-dot">{pkgDiscountPct}%</span>
                : <span style={{ color: "var(--border-strong)", fontWeight: 400 }}>—</span>}
            </InfoRow>
            <InfoRow label={t("Jumlah Student")}>
              {pkgJumlahStudent > 0
                ? <span style={{ fontWeight: 600 }}>{pkgJumlahStudent.toLocaleString("id-ID")} {t("siswa")}</span>
                : <span style={{ color: "var(--border-strong)", fontWeight: 400 }}>—</span>}
            </InfoRow>
            <InfoRow label={t("Storage")} last>
              {pkgStorageGb > 0
                ? <span style={{ fontWeight: 600 }}>{pkgStorageGb} GB</span>
                : <span style={{ color: "var(--border-strong)", fontWeight: 400 }}>—</span>}
            </InfoRow>
          </SectionCard>

          <SectionCard icon={<FileIcon />} title={t("Lampiran & Catatan")}>
            <InfoRow label={t("Keterangan Lampiran")} value={tenant.lampiran} />
            {attachmentUrl && (
              <div style={{ padding: "12px 0", borderBottom: "1px solid var(--border-subtle)" }}>
                <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500, marginBottom: 8 }}>{t("File Terlampir")}</div>
                <div style={{
                  display: "flex", alignItems: "center", gap: 12,
                  background: "var(--slate-50)", border: "1px solid var(--border-subtle)",
                  borderRadius: 10, padding: "10px 14px",
                }}>
                  <span style={{ color: "var(--text-muted)" }}><FileIcon /></span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-strong)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {attachmentFileName || "attachment"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <a className="lms-btn lms-btn-secondary lms-btn-sm" href={attachmentUrl} target="_blank" rel="noopener noreferrer">
                      <EyeIcon /> {t("View")}
                    </a>
                    <button className="lms-btn lms-btn-primary lms-btn-sm" onClick={handleDownloadAttachment} disabled={downloadingAttachment}>
                       {downloadingAttachment ? <span className="lms-spinner" style={{ width: 12, height: 12 }} /> : <DownloadIcon />}
                       {t("Download")}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <InfoRow label={t("Catatan")} last>
              {tenant.remark
                ? <span style={{ whiteSpace: "pre-wrap" }}>{tenant.remark}</span>
                : <span style={{ color: "var(--border-strong)", fontWeight: 400 }}>—</span>}
            </InfoRow>
          </SectionCard>
        </div>

        {/* ─ Right column ─ */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Package & Pricing */}
          <SectionCard icon={<TagIcon />} title={t("Paket & Harga")}>
            {packageLoading ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0" }}>
                <div className="lms-spinner" style={{ width: 16, height: 16 }} />
                <span className="t-small lms-text-muted">{t("Memuat paket...")}</span>
              </div>
            ) : selectedPackage ? (
              <div>
                    {/* Package name + discount badge */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-strong)" }}>{selectedPackage.name}</div>
                      {pkgDiscountPct > 0 && <span className="lms-badge lms-badge-danger">-{pkgDiscountPct}%</span>}
                    </div>

                    {/* Price breakdown */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 7, borderTop: "1px solid var(--border-subtle)", paddingTop: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--text-muted)" }}>
                         <span>{t("Harga paket")}</span>
                        <span style={{
                          fontWeight: 500,
                          color: pkgDiscountPct > 0 ? "var(--danger-400)" : "var(--text-base)",
                          textDecoration: pkgDiscountPct > 0 ? "line-through" : "none",
                        }}>
                          Rp {displayBase.toLocaleString("id-ID")}
                        </span>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--text-muted)" }}>
                         <span>{t("Diskon")}{pkgDiscountPct > 0 ? ` (${pkgDiscountPct}%)` : ""}</span>
                        <span style={{ fontWeight: 500, color: pkgDiscountPct > 0 ? "var(--success-600)" : "var(--text-base)" }}>
                          {pkgDiscountPct > 0 ? `-Rp ${(pkgBasePrice - pkgDiscountedPrice).toLocaleString("id-ID")}` : "Rp 0"}
                        </span>
                      </div>

                      {pkgJumlahStudent > 0 && (
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--text-muted)" }}>
                          <span>{t("Jumlah Student")}</span>
                          <span style={{ fontWeight: 500, color: "var(--text-base)" }}>{pkgJumlahStudent.toLocaleString("id-ID")} {t("siswa")}</span>
                        </div>
                      )}

                      {pkgStorageGb > 0 && (
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--text-muted)" }}>
                          <span>{t("Storage")}</span>
                          <span style={{ fontWeight: 500, color: "var(--text-base)" }}>{pkgStorageGb} GB</span>
                        </div>
                      )}

                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--text-muted)" }}>
                         <span>{t("Biaya implementasi")}</span>
                        <span style={{ fontWeight: 500, color: "var(--text-base)" }}>Rp {pkgInstallation.toLocaleString("id-ID")}</span>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, borderTop: "1px solid var(--border-subtle)" }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-strong)" }}>{t("Total")}</span>
                        <span style={{ fontSize: 16, fontWeight: 700, color: "var(--brand-600)" }}>Rp {pkgTotal.toLocaleString("id-ID")}</span>
                      </div>
                    </div>

                    {/* Benefits */}
                    {selectedPackage.benefits?.length > 0 && (
                      <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border-subtle)", display: "flex", flexDirection: "column", gap: 6 }}>
                        {selectedPackage.benefits.map((b, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, color: "var(--text-base)" }}>
                            <span style={{ color: "var(--success-500)", flexShrink: 0, marginTop: 2 }}><CheckIcon /></span>
                            {b}
                          </div>
                        ))}
                      </div>
                    )}
              </div>
            ) : (
              <div className="t-small lms-text-muted" style={{ padding: "8px 0" }}>{t("Tidak ada informasi paket")}</div>
            )}
          </SectionCard>

          {/* PICs */}
          <SectionCard
            icon={<UsersIcon />}
            title={`${t("Person In Charge")}${tenant.pics?.length ? ` (${tenant.pics.length})` : ""}`}
          >
            {tenant.pics?.length > 0 ? (
              tenant.pics.map((pic, i) => (
                <ContactRow
                  key={pic.id || i}
                  name={pic.nama || pic.name}
                  email={pic.email}
                  phone={pic.nomorTelepon || pic.phone}
                  index={i}
                  last={i === tenant.pics.length - 1}
                />
              ))
            ) : (
              <div className="t-small lms-text-muted" style={{ textAlign: "center", padding: "16px 0" }}>
                 {t("Belum ada PIC")}
              </div>
            )}
          </SectionCard>

          {/* Invitations */}
          <SectionCard
            icon={<MailIcon />}
            title={`${t("Undangan")}${tenant.invitations?.length ? ` (${tenant.invitations.length})` : ""}`}
          >
            {tenant.invitations?.length > 0 ? (
              tenant.invitations.map((inv, i) => {
                const s = (inv.status || "").toLowerCase();
                const sVariant = s === "accepted" ? "success" : s === "pending" ? "warning" : s === "sent" ? "info" : s === "expired" ? "danger" : "neutral";
                return (
                  <ContactRow
                    key={inv.id || i}
                    name={inv.name || inv.nama}
                    email={inv.email}
                    phone={inv.phone || inv.nomorTelepon}
                    index={i + (tenant.pics?.length || 0)}
                    badge={inv.status && <span className={`lms-badge lms-badge-${sVariant}`} style={{ fontSize: 10 }}>{inv.status}</span>}
                    last={i === tenant.invitations.length - 1}
                  />
                );
              })
            ) : (
              <div className="t-small lms-text-muted" style={{ textAlign: "center", padding: "16px 0" }}>
                 {t("Tidak ada undangan")}
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      {/* ── Tenant Data Tabs ── */}
      <div className="lms-card" style={{ marginTop: 20, marginBottom: 0 }}>
        <div className="lms-card-header">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "var(--brand-500)" }}><UsersIcon /></span>
            <div className="t-h2" style={{ fontSize: 14.5 }}>{t("Data Tenant")}</div>
          </div>
        </div>
        <div style={{ borderBottom: "1px solid var(--border-subtle)", padding: "0 22px" }}>
          <div style={{ display: "flex", gap: 0, overflow: "hidden" }}>
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: "12px 20px", border: "none", background: "none", cursor: "pointer",
                  fontSize: 13, fontWeight: activeTab === tab.key ? 600 : 500,
                  color: activeTab === tab.key ? "var(--brand-600)" : "var(--text-muted)",
                  borderBottom: activeTab === tab.key ? "2px solid var(--brand-500)" : "2px solid transparent",
                  transition: "color 0.15s, border-color 0.15s",
                }}
              >
                {tab.label}
                {(tabMeta[tab.key]?.total ?? tabData[tab.key]?.length) > 0 && (
                  <span style={{ marginLeft: 6, fontSize: 11, background: activeTab === tab.key ? "var(--brand-50)" : "var(--slate-100)", color: activeTab === tab.key ? "var(--brand-600)" : "var(--text-muted)", borderRadius: 8, padding: "1px 7px" }}>
                    {tabMeta[tab.key]?.total ?? tabData[tab.key].length}
                  </span>
                )}
              </button>
            ))}
           </div>
        </div>
        <div className="lms-card-body" style={{ padding: 16 }}>
          {tabLoading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 40, gap: 10 }}>
              <div className="lms-spinner" style={{ width: 20, height: 20 }} />
              <span className="t-small lms-text-muted">{t("Memuat data...")}</span>
            </div>
          ) : (
            <>
              {activeTab === "roles" && (
                <div style={{ overflowX: "auto" }}>
                  <table className="lms-table" style={{ width: "100%", fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}>#</th>
                        <th>Name</th>
                        <th>Type</th>
                        <th style={{ width: 100 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tabData.roles.length === 0 ? (
                         <tr><td colSpan={4} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>{t("Tidak ada data roles")}</td></tr>
                      ) : tabData.roles.map((r, i) => (
                        <tr key={r.id}>
                          <td style={{ color: "var(--text-muted)" }}>{i + 1}</td>
                          <td style={{ fontWeight: 500 }}>{r.name}</td>
                          <td><span style={{ fontSize: 11.5, background: "var(--slate-100)", color: "var(--text-muted)", borderRadius: 5, padding: "2px 8px" }}>{r.type || "—"}</span></td>
                          <td><span className={`lms-badge lms-badge-${r.is_active ? "success" : "neutral"}`}>{r.is_active ? "Active" : "Inactive"}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === "users" && (
                <div style={{ overflowX: "auto" }}>
                  <table className="lms-table" style={{ width: "100%", fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th style={{ width: 100 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tabData.users.length === 0 ? (
                         <tr><td colSpan={5} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>{t("Tidak ada data users")}</td></tr>
                      ) : tabData.users.map((u, i) => (
                        <tr key={u.id}>
                          <td style={{ color: "var(--text-muted)" }}>{i + 1}</td>
                          <td style={{ fontWeight: 500 }}>{u.full_name || "—"}</td>
                          <td style={{ color: "var(--text-muted)" }}>{u.email || "—"}</td>
                          <td><span style={{ fontSize: 11.5, background: "var(--brand-50)", color: "var(--brand-700)", borderRadius: 5, padding: "2px 8px" }}>{u.role?.name || "—"}</span></td>
                          <td><span className={`lms-badge lms-badge-${u.is_active ? "success" : "neutral"}`}>{u.is_active ? "Active" : "Inactive"}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === "teachers" && (
                <div style={{ overflowX: "auto" }}>
                  <table className="lms-table" style={{ width: "100%", fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th style={{ width: 100 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tabData.teachers.length === 0 ? (
                         <tr><td colSpan={5} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>{t("Tidak ada data teachers")}</td></tr>
                      ) : tabData.teachers.map((tc, i) => (
                        <tr key={tc.id}>
                          <td style={{ color: "var(--text-muted)" }}>{i + 1}</td>
                          <td style={{ fontWeight: 500 }}>{tc.full_name || "—"}</td>
                          <td style={{ color: "var(--text-muted)" }}>{tc.email || "—"}</td>
                          <td style={{ color: "var(--text-muted)" }}>{tc.phone || "—"}</td>
                          <td><span className={`lms-badge lms-badge-${tc.is_active ? "success" : "neutral"}`}>{tc.is_active ? "Active" : "Inactive"}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === "students" && (
                <div style={{ overflowX: "auto" }}>
                  <table className="lms-table" style={{ width: "100%", fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}>#</th>
                        <th>Name</th>
                        <th>NIS</th>
                        <th>NISN</th>
                        <th>Type</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tabData.students.length === 0 ? (
                         <tr><td colSpan={6} style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}>{t("Tidak ada data students")}</td></tr>
                      ) : tabData.students.map((s, i) => (
                        <tr key={s.id}>
                          <td style={{ color: "var(--text-muted)" }}>{i + 1}</td>
                          <td style={{ fontWeight: 500 }}>{[s.student_first_name, s.student_last_name].filter(Boolean).join(" ") || "—"}</td>
                          <td style={{ color: "var(--text-muted)" }}>{s.nis || "—"}</td>
                          <td style={{ color: "var(--text-muted)" }}>{s.nisn || "—"}</td>
                          <td><span style={{ fontSize: 11.5, background: "var(--slate-100)", color: "var(--text-muted)", borderRadius: 5, padding: "2px 8px" }}>{s.student_type || "—"}</span></td>
                          <td><span style={{ fontSize: 11.5, background: "var(--brand-50)", color: "var(--brand-700)", borderRadius: 5, padding: "2px 8px" }}>{s.student_status || "—"}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {tabMeta[activeTab] && tabMeta[activeTab].last_page > 1 && (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 16 }}>
                  <button
                    className="lms-btn lms-btn-secondary lms-btn-sm"
                    disabled={tabMeta[activeTab].current_page <= 1}
                    onClick={() => fetchTabData(activeTab, tabMeta[activeTab].current_page - 1)}
                  >Prev</button>
                  <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {tabMeta[activeTab].current_page} / {tabMeta[activeTab].last_page}
                  </span>
                  <button
                    className="lms-btn lms-btn-secondary lms-btn-sm"
                    disabled={tabMeta[activeTab].current_page >= tabMeta[activeTab].last_page}
                    onClick={() => fetchTabData(activeTab, tabMeta[activeTab].current_page + 1)}
                  >Next</button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Add PIC Modal ── */}
      {isPicModalOpen && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)", zIndex: 90, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => setIsPicModalOpen(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: 20, boxShadow: "0 24px 48px -12px rgba(15,23,42,0.18)", width: "100%", maxWidth: 480, overflow: "hidden" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 11, background: "var(--brand-50)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--brand-500)", flexShrink: 0 }}>
                  <UsersIcon />
                </div>
                <div>
                  <div className="t-h2">{t("Tambah PIC")}</div>
                  <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 1 }}>{tenant.namaTenant}</div>
                </div>
              </div>
              <button
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex", alignItems: "center", padding: 6, borderRadius: 8 }}
                onClick={() => setIsPicModalOpen(false)}
              >
                <XIcon />
              </button>
            </div>
            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
              <div className="lms-form-group">
                <label className="lms-label">{t("Nama")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                <input className="lms-input" value={picForm.name} onChange={(e) => setPicForm({ ...picForm, name: e.target.value })} placeholder={t("Masukkan nama PIC")} />
              </div>
              <div className="lms-form-group">
                <label className="lms-label">{t("Email")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                <input className="lms-input" type="email" value={picForm.email} onChange={(e) => setPicForm({ ...picForm, email: e.target.value })} placeholder="pic@example.com" />
              </div>
              <div className="lms-form-group">
                <label className="lms-label">{t("No. Telepon")} <span style={{ color: "var(--danger-500)" }}>*</span></label>
                <input className="lms-input" value={picForm.phone} onChange={(e) => setPicForm({ ...picForm, phone: e.target.value })} placeholder="08xxxxxxxxxx" />
              </div>
            </div>
            <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button className="lms-btn lms-btn-secondary" onClick={() => setIsPicModalOpen(false)}>{t("Batal")}</button>
              <button className="lms-btn lms-btn-primary" onClick={submitNewPic} disabled={picSubmitting}>
                {picSubmitting ? <span className="lms-spinner" style={{ width: 14, height: 14 }} /> : <PlusIcon />}
                {picSubmitting ? t("Menyimpan...") : t("Simpan PIC")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TenantDetail;
